import { useState } from "react";
import axios from "axios";
import { Check, Loader2, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { useReveal } from "../hooks/useReveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const flatServiceOptions = (t) => {
    const opts = [];
    (t.services?.groups || []).forEach((g) =>
        g.items.forEach((it) => opts.push({ id: it.id, label: it.title }))
    );
    opts.unshift({ id: "purchasing", label: t.nav.purchasing });
    return opts;
};

export default function Contact() {
    const { t, lang } = useLang();
    const { ref, className } = useReveal();
    const [form, setForm] = useState({
        name: "",
        company: "",
        email: "",
        phone: "",
        service: "",
        message: "",
    });
    const [state, setState] = useState({
        loading: false,
        success: false,
        error: "",
    });

    const handle = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            setState({
                loading: false,
                success: false,
                error: t.contact.requiredError,
            });
            return;
        }
        setState({ loading: true, success: false, error: "" });
        try {
            await axios.post(`${API}/contact`, { ...form, language: lang });
            setState({ loading: false, success: true, error: "" });
            setForm({
                name: "",
                company: "",
                email: "",
                phone: "",
                service: "",
                message: "",
            });
        } catch (err) {
            setState({
                loading: false,
                success: false,
                error: t.contact.errorMsg,
            });
        }
    };

    const info = t.contact.info;
    const services = flatServiceOptions(t);

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="py-24 md:py-32 bg-white"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div
                    ref={ref}
                    className={`${className} grid lg:grid-cols-12 gap-12 lg:gap-16`}
                >
                    {/* Left: copy + info */}
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-10 h-px bg-ink" />
                            <span className="font-display text-xs uppercase tracking-[0.3em] text-ink-muted">
                                {t.contact.kicker}
                            </span>
                        </div>
                        <h2 className="font-display font-bold uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-ink">
                            {t.contact.title}
                        </h2>
                        <p className="mt-6 text-base md:text-lg text-ink-soft max-w-md">
                            {t.contact.subtitle}
                        </p>

                        <div className="mt-10 space-y-5">
                            <div className="flex items-start gap-4">
                                <span className="w-10 h-10 bg-ink text-mint flex items-center justify-center flex-shrink-0">
                                    <Mail size={18} />
                                </span>
                                <div>
                                    <div className="font-display text-xs uppercase tracking-widest text-ink-muted">
                                        {info.emailLabel}
                                    </div>
                                    {info.emails.map((em) => (
                                        <a
                                            key={em}
                                            href={`mailto:${em}`}
                                            className="block text-base text-ink hover:text-mint-hover"
                                            data-testid={`contact-info-email-${em.split("@")[0]}`}
                                        >
                                            {em}
                                        </a>
                                    ))}
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="w-10 h-10 bg-ink text-mint flex items-center justify-center flex-shrink-0">
                                    <MapPin size={18} />
                                </span>
                                <div>
                                    <div className="font-display text-xs uppercase tracking-widest text-ink-muted">
                                        {info.addressLabel}
                                    </div>
                                    <div className="text-base text-ink">
                                        {info.address}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="w-10 h-10 bg-ink text-mint flex items-center justify-center flex-shrink-0">
                                    <Clock size={18} />
                                </span>
                                <div>
                                    <div className="font-display text-xs uppercase tracking-widest text-ink-muted">
                                        {info.replyLabel}
                                    </div>
                                    <div className="text-base text-ink">
                                        {info.replyValue}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* WhatsApp CTA */}
                        <a
                            href={info.whatsappLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-whatsapp"
                            className="mt-8 inline-flex items-center gap-3 bg-mint text-ink hover:bg-mint-hover transition-colors px-6 py-4 border-2 border-ink font-display uppercase tracking-widest text-sm group"
                        >
                            <MessageCircle size={20} />
                            <span>{info.whatsappCta}</span>
                        </a>
                    </div>

                    {/* Right: form */}
                    <div className="lg:col-span-7">
                        <form
                            onSubmit={submit}
                            data-testid="contact-form"
                            className="bg-paper-alt border border-ink p-6 md:p-10"
                        >
                            {state.success ? (
                                <div
                                    data-testid="contact-success"
                                    className="text-center py-16"
                                >
                                    <span className="inline-flex w-16 h-16 bg-mint items-center justify-center mb-6">
                                        <Check size={32} className="text-ink" />
                                    </span>
                                    <h3 className="font-display font-bold uppercase text-2xl md:text-3xl text-ink">
                                        {t.contact.successTitle}
                                    </h3>
                                    <p className="mt-4 text-ink-soft max-w-md mx-auto">
                                        {t.contact.successMsg}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div>
                                            <label className="label">
                                                {t.contact.fields.name} *
                                            </label>
                                            <input
                                                data-testid="contact-name"
                                                name="name"
                                                value={form.name}
                                                onChange={handle}
                                                className="field"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="label">
                                                {t.contact.fields.company}
                                            </label>
                                            <input
                                                data-testid="contact-company"
                                                name="company"
                                                value={form.company}
                                                onChange={handle}
                                                className="field"
                                            />
                                        </div>
                                        <div>
                                            <label className="label">
                                                {t.contact.fields.email} *
                                            </label>
                                            <input
                                                data-testid="contact-email"
                                                type="email"
                                                name="email"
                                                value={form.email}
                                                onChange={handle}
                                                className="field"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="label">
                                                {t.contact.fields.phone}
                                            </label>
                                            <input
                                                data-testid="contact-phone"
                                                name="phone"
                                                value={form.phone}
                                                onChange={handle}
                                                className="field"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="label">
                                                {t.contact.fields.service}
                                            </label>
                                            <select
                                                data-testid="contact-service"
                                                name="service"
                                                value={form.service}
                                                onChange={handle}
                                                className="field"
                                            >
                                                <option value="">
                                                    {t.contact.fields.serviceDefault}
                                                </option>
                                                {services.map((s) => (
                                                    <option
                                                        key={s.id}
                                                        value={s.label}
                                                    >
                                                        {s.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="label">
                                                {t.contact.fields.message} *
                                            </label>
                                            <textarea
                                                data-testid="contact-message"
                                                name="message"
                                                value={form.message}
                                                onChange={handle}
                                                rows={5}
                                                className="field resize-none"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {state.error && (
                                        <div
                                            data-testid="contact-error"
                                            className="mt-5 px-4 py-3 border border-destructive text-destructive text-sm"
                                        >
                                            {state.error}
                                        </div>
                                    )}

                                    <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                                        <p className="text-xs text-ink-muted">
                                            * {lang === "fr"
                                                ? "Champs obligatoires"
                                                : "Required fields"}
                                        </p>
                                        <button
                                            type="submit"
                                            data-testid="contact-submit"
                                            disabled={state.loading}
                                            className="btn-primary disabled:opacity-50"
                                        >
                                            {state.loading ? (
                                                <>
                                                    <Loader2
                                                        size={16}
                                                        className="animate-spin"
                                                    />
                                                    {t.contact.submitting}
                                                </>
                                            ) : (
                                                t.contact.submit
                                            )}
                                        </button>
                                    </div>
                                </>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}
